// Client du moteur SQL des leçons Reps : jumeau de jsRunner.js, avec la même
// forme de résultat ({ ok, stdout, error, timedOut, unavailable }) plus `sql`,
// la sortie complète de RepsSql.executer (public/static/reps/sql-format.js) :
// le tableau, celui de la référence, et le verdict de la comparaison.
//
// Un seul worker garde sql.js chargé (700 Ko, 30 ms au démarrage) : chaque
// exécution ouvre de toute façon une base neuve. Il n'est tué que sur délai
// dépassé, et le suivant repart de zéro.

const WORKER_URL = "/static/reps/sql-worker.js";
// Même délai que JavaScript : une jointure croisée de 27 millions de lignes
// prend 2,2 s sur un Mac (fait 12), davantage sur un téléphone.
const RUN_TIMEOUT_MS = 4000;
// Le premier démarrage télécharge le wasm : il a droit à plus.
const BOOT_TIMEOUT_MS = 15000;

const TROP_LONG = "Ta requête a mis trop de temps. Exécution arrêtée.";

let worker = null;
let pret = null;
let compteur = 0;
const enCours = new Map();
let queue = Promise.resolve();

function indisponible(error) {
    return { stdout: "", stderr: "", ok: false, timedOut: false, unavailable: true, error, sql: null };
}

function demarrer() {
    if (worker) return pret;
    if (typeof Worker === "undefined") {
        pret = Promise.reject(new Error("Worker indisponible."));
        pret.catch(() => {});
        return pret;
    }
    let w;
    try {
        w = new Worker(WORKER_URL);
    } catch (e) {
        pret = Promise.reject(e);
        pret.catch(() => {});
        return pret;
    }
    worker = w;
    pret = new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error("sql.js n'a pas démarré.")), BOOT_TIMEOUT_MS);
        w.onmessage = (e) => {
            const msg = e.data || {};
            if (msg.type === "ready") {
                clearTimeout(timer);
                resolve();
                return;
            }
            if (msg.type === "init_error") {
                clearTimeout(timer);
                reject(new Error(msg.text || "sql.js"));
                return;
            }
            if (msg.type === "done") {
                const attente = enCours.get(msg.id);
                if (attente) attente(msg.sortie);
            }
        };
        w.onerror = (ev) => {
            if (ev && ev.preventDefault) ev.preventDefault();
            clearTimeout(timer);
            reject(new Error(String((ev && ev.message) || "worker")));
        };
    });
    pret.catch(() => tuer());
    return pret;
}

function tuer() {
    if (worker) worker.terminate();
    worker = null;
    pret = null;
}

/**
 * Exécute une requête sur une base neuve. `options` = celles de
 * RepsSql.executer : { base, seed, requete, reference?, tables?, ordre?,
 * noms?, avecTables? }. Ne rejette jamais. Les appels sont sérialisés.
 */
export function runSql(options) {
    const next = queue.then(() => runNow(options || {}));
    queue = next.catch(() => {});
    return next;
}

async function runNow(options) {
    try {
        await demarrer();
    } catch (e) {
        tuer();
        return indisponible(String((e && e.message) || e));
    }
    const w = worker;
    const id = ++compteur;
    return new Promise((resolve) => {
        const timer = setTimeout(() => {
            enCours.delete(id);
            // sql.js ne s'interrompt pas : on tue le worker, le suivant
            // rechargera le moteur.
            if (worker === w) tuer();
            resolve({ stdout: "", stderr: "", ok: false, timedOut: true, error: TROP_LONG, sql: null });
        }, RUN_TIMEOUT_MS);
        enCours.set(id, (sortie) => {
            clearTimeout(timer);
            enCours.delete(id);
            resolve(versResultat(sortie));
        });
        w.postMessage({ type: "run", id, options });
    });
}

// La forme commune des consoles. Une erreur de la BASE (script de la leçon)
// n'est pas celle de l'apprenant : elle passe quand même dans `error`, pour
// qu'un auteur la voie, mais `ok` reste faux.
export function versResultat(sortie) {
    const s = sortie || {};
    const r = s.resultat;
    if (s.erreurBase) {
        return { stdout: "", stderr: "", ok: false, timedOut: false, error: s.erreurBase, sql: s };
    }
    return {
        stdout: "",
        stderr: "",
        ok: !!(r && r.ok),
        timedOut: false,
        error: r && !r.ok ? r.erreur : undefined,
        sql: s,
    };
}

/** Charge le moteur avant le premier écran qui exécute. */
export function warmSql() {
    demarrer().catch(() => {});
}
